// ============================================================
// /customers — 顧客データベース管理
//
// 正: booking-platform/md/customers.md
// バックエンド: sys/customer/api.php（API_KEY_INTERNAL 認証）
//
// 顧客は予約データから自動生成される（15分 cron = sys/mail/inbox_worker.php）。
// この画面の役割は「自動で決められなかったことを人が決める」こと:
//   - 統合候補の判断（同一人物か別人か）
//   - メイン/サブメールの管理、メモ
//   - 顧客 → 予約 → Square請求 の串刺し確認
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Chip, Button, TextField, LinearProgress, Alert, Snackbar,
    Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Tabs, Tab, TableSortLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Pagination,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { LINE_SYNC_KEY } from '../config/instances';
import { getOperatorName } from './reconcile/systemApi';
import { logEvent } from '../lib/eventLog';
import ReservationEditorPanel from '../components/editor2';
import { toEditorReservation } from '../lib/editorDataTransform';
import { saveDispatchReservation, fetchDispatchReservations, fetchDispatchVehicles } from '../api/dispatchService';
import { useReservationStatuses } from '../hooks/useReservationStatuses';
import { setPageContext, clearPageContext } from '../agent/pageContext';

const API_URL = process.env.REACT_APP_CUSTOMER_API_URL
    || 'https://booking.trailernchu.co.jp/sys/customer/api.php';

interface CustomerRow {
    id: number;
    display_name: string;
    name_kana: string | null;
    memo: string | null;
    email: string | null;
    email_count: number;
    tel: string | null;
    /** 有効な予約の台数（非予約ステータスは除く） */
    reservation_count: number;
    /** 別々の日に来た回数。reservation_count と違い、同日複数台は1回と数える */
    visit_count: number;
    /** 非予約（キャンセル等）の件数。台数には数えないが履歴として見せる */
    cancelled_count: number;
    first_seen_at: string | null;
    last_seen_at: string | null;
}

interface EmailRow { email: string; is_primary: number; is_shared: number; source: string; added_at: string; }
interface TelRow { tel_raw: string; tel_norm: string; is_primary: number; source: string; }
interface ReservationRow {
    contact_code: string; base_code: string; store: string;
    start_at: string | null; link_source: string; confidence: number;
    status: number | null; status_code: string | null;
    /** 1=予約 / 2=非予約（キャンセル等・台数に数えない） / 3=完了済予約 */
    status_category: number | null;
}
interface GroupRow { group_code: string; store: string; origin: string; rep_contact_code: string | null; note: string | null; members: string | null; }
interface MailRow { mail_id: string; sender: string | null; subject: string | null; received_at: string | null; contact_code: string | null; }
interface InvoiceRow { [k: string]: any; }

interface CustomerDetail extends CustomerRow {
    emails: EmailRow[];
    tels: TelRow[];
    reservations: ReservationRow[];
    groups: GroupRow[];
    mails: MailRow[];
    invoices: InvoiceRow[];
    invoices_error?: string;
    merged_from: { id: number; display_name: string }[];
}

interface Candidate {
    id: number; customer_a: number; customer_b: number;
    matched_on: string; score: number; detail: string | null;
    name_a: string; name_b: string;
    email_a: string | null; email_b: string | null;
    tel_a: string | null; tel_b: string | null;
    res_a: number; res_b: number;
}

interface Stats {
    customers: number;
    /** 別の日にも来ている人。同日複数台は含まない */
    repeaters: number;
    /** 複数台を予約している人（同日含む） */
    multi_car: number;
    candidates: number; groups: number; merged: number;
    last_synced_at: string | null;
}

const STORE_LABEL: Record<string, string> = { ncar: '那覇', pit: '宮古' };

// 一覧の列。key は sys/customer/api.php の並び替えホワイトリストと一致させること
const LIST_COLUMNS: { key: string; label: string; align: 'left' | 'right'; hint: string }[] = [
    { key: 'name', label: '氏名', align: 'left', hint: 'クリックで並び替え' },
    { key: 'email', label: 'メール', align: 'left', hint: 'クリックで並び替え' },
    { key: 'tel', label: '電話', align: 'left', hint: 'クリックで並び替え' },
    { key: 'visits', label: '来店回数', align: 'right', hint: '別々の日に来た回数（同日の複数台は1回）' },
    { key: 'reservations', label: '台数', align: 'right', hint: '有効な予約の台数（キャンセル等は数えない）' },
    { key: 'cancelled', label: '非予約', align: 'right', hint: 'キャンセル等の件数（台数には数えない）' },
    { key: 'recent', label: '最終利用', align: 'left', hint: 'クリックで並び替え' },
];

const fmtDate = (s: string | null) => {
    if (!s) return '—';
    const d = new Date(s.replace(' ', 'T'));
    return isNaN(d.getTime()) ? s : d.toLocaleDateString('ja-JP');
};

const Customers: React.FC = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<CustomerRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState('');
    const [queryInput, setQueryInput] = useState('');
    const [sort, setSort] = useState<string>('recent');
    const [dir, setDir] = useState<'asc' | 'desc'>('desc');
    // 絞り込み: '' = 全員 / 'multi_car' = 複数台予約 / 'repeat' = リピーター
    const [filter, setFilter] = useState<'' | 'multi_car' | 'repeat'>('');
    const [stats, setStats] = useState<Stats | null>(null);
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [detail, setDetail] = useState<CustomerDetail | null>(null);
    const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [mergeTarget, setMergeTarget] = useState<Candidate | null>(null);
    // 予約サイドパネル（MailQueue / LineChatBoard と同じ埋め込み方式）
    const [selectedRes, setSelectedRes] = useState<any | null>(null);
    const [editingRes, setEditingRes] = useState<any | null>(null);
    const [isSavingRes, setIsSavingRes] = useState(false);
    const [vehiclesCache, setVehiclesCache] = useState<Record<string, any>>({});
    const { reservationStatusList, vehicleTypeList } = useReservationStatuses();
    const PER_PAGE = 50;

    // エージェントに「顧客ページ・開いている顧客」を伝える（ai-control-center.md §15）
    useEffect(() => {
        setPageContext({
            page: 'customers',
            selection: detail ? {
                type: 'customer',
                id: String(detail.id),
                label: `顧客 ${detail.display_name} 様（予約${detail.reservation_count}件 / 来店${detail.visit_count}回）`,
            } : undefined,
        });
        return () => clearPageContext('customers');
    }, [detail]);

    const call = useCallback(async (action: string, opts: { method?: string; body?: any; params?: Record<string, string> } = {}) => {
        const params = new URLSearchParams({ action, api_key: LINE_SYNC_KEY, ...(opts.params || {}) });
        const res = await fetch(`${API_URL}?${params.toString()}`, {
            method: opts.method || 'GET',
            headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
            body: opts.body ? JSON.stringify({ ...opts.body, operator: getOperatorName() || 'unknown' }) : undefined,
        });
        const json = await res.json().catch(() => ({ success: false, error: '応答を解釈できませんでした' }));
        if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`);
        return json;
    }, []);

    const loadStats = useCallback(async () => {
        try {
            const j = await call('stats');
            setStats(j.stats);
        } catch (e: any) {
            // 無言の失敗を作らない（md/loop-engineering.md）
            logEvent({ action: 'customer.stats', target: 'api', result: 'fail', err: e.message });
            setError(`サマリの取得に失敗しました: ${e.message}`);
        }
    }, [call]);

    const loadList = useCallback(async (p = page, query = q, s = sort, d = dir, f = filter) => {
        setLoading(true);
        setError(null);
        try {
            const j = await call('list', {
                params: { page: String(p), per_page: String(PER_PAGE), q: query, sort: s, dir: d, filter: f },
            });
            setRows(j.customers);
            setTotal(j.total);
        } catch (e: any) {
            logEvent({ action: 'customer.list', target: 'api', result: 'fail', err: e.message });
            setError(`顧客一覧の取得に失敗しました: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, [call, page, q, sort, dir, filter]);

    const loadCandidates = useCallback(async () => {
        setLoading(true);
        try {
            const j = await call('candidates');
            setCandidates(j.candidates);
        } catch (e: any) {
            logEvent({ action: 'customer.candidates', target: 'api', result: 'fail', err: e.message });
            setError(`統合候補の取得に失敗しました: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, [call]);

    // 初回のみ読み込む。loadStats/loadList を依存に入れると毎レンダーで再取得されるため意図的に空配列。
    // ※ react-hooks プラグインはこのプロジェクトの eslint 設定に入っていないので抑制コメントは書かない
    useEffect(() => { loadStats(); loadList(1, ''); }, []);
    // 統合候補タブに切り替わったときだけ取得する
    useEffect(() => { if (tab === 1) loadCandidates(); }, [tab]);

    useEffect(() => {
        fetchDispatchVehicles().then((data: any[]) => {
            const m: Record<string, any> = {};
            (data || []).forEach((v) => { m[String(v.id)] = v; });
            setVehiclesCache(m);
        }).catch((e: any) => {
            logEvent({ action: 'customer.vehicles', target: 'dispatch', result: 'fail', err: e?.message });
        });
    }, []);

    useEffect(() => { setEditingRes(selectedRes ? toEditorReservation(selectedRes) : null); }, [selectedRes]);

    /** 予約コードから完全な予約データを取ってサイドパネルを開く */
    const openReservation = async (code: string) => {
        setBusy(true);
        try {
            const list = await fetchDispatchReservations({ contactCode: code });
            const hit = (list || []).find((r: any) => String(r.contact_code) === code) || null;
            if (!hit) { setToast({ msg: `予約 ${code} が見つかりません`, sev: 'error' }); return; }
            setSelectedRes(hit);
        } catch (e: any) {
            logEvent({ action: 'customer.open_reservation', target: code, result: 'fail', err: e?.message });
            setToast({ msg: `予約の取得に失敗しました: ${e?.message || e}`, sev: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const openDetail = async (id: number) => {
        setBusy(true);
        try {
            const j = await call('detail', { params: { id: String(id) } });
            setDetail(j.customer);
        } catch (e: any) {
            logEvent({ action: 'customer.detail', target: String(id), result: 'fail', err: e.message });
            setToast({ msg: `顧客情報の取得に失敗しました: ${e.message}`, sev: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const doSearch = () => { setQ(queryInput); setPage(1); loadList(1, queryInput, sort, dir); };

    /** 統計チップから絞り込む。同じチップをもう一度押すと解除 */
    const changeFilter = (f: 'multi_car' | 'repeat') => {
        const next = filter === f ? '' : f;
        setFilter(next); setPage(1); setTab(0);
        loadList(1, q, sort, dir, next);
    };

    /** 同じ列をもう一度押すと昇順・降順が入れ替わる */
    const changeSort = (key: string) => {
        const nextDir: 'asc' | 'desc' = sort === key && dir === 'desc' ? 'asc' : 'desc';
        setSort(key); setDir(nextDir); setPage(1);
        loadList(1, q, key, nextDir);
    };

    const doSync = async () => {
        setBusy(true);
        try {
            const j = await call('sync', { method: 'POST', body: {} });
            setToast({ msg: `同期しました（新規 ${j.stat.new} 人 / 既存へ紐づけ ${j.stat.linked} 件）`, sev: 'success' });
            loadStats(); loadList(page, q, sort, dir);
        } catch (e: any) {
            logEvent({ action: 'customer.sync', target: 'manual', result: 'fail', err: e.message });
            setToast({ msg: `同期に失敗しました: ${e.message}`, sev: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const doMerge = async (from: number, into: number) => {
        setBusy(true);
        try {
            await call('merge', { method: 'POST', body: { from, into } });
            setToast({ msg: '統合しました', sev: 'success' });
            setMergeTarget(null);
            loadCandidates(); loadStats(); loadList(page, q, sort, dir);
        } catch (e: any) {
            logEvent({ action: 'customer.merge', target: `${from}->${into}`, result: 'fail', err: e.message });
            setToast({ msg: `統合に失敗しました: ${e.message}`, sev: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const doReject = async (id: number) => {
        setBusy(true);
        try {
            await call('reject', { method: 'POST', body: { id } });
            setToast({ msg: '別人として記録しました', sev: 'success' });
            loadCandidates(); loadStats();
        } catch (e: any) {
            logEvent({ action: 'customer.candidate.reject', target: String(id), result: 'fail', err: e.message });
            setToast({ msg: `処理に失敗しました: ${e.message}`, sev: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const setPrimary = async (id: number, email: string) => {
        try {
            await call('set_primary_email', { method: 'POST', body: { id, email } });
            setToast({ msg: 'メインのメールアドレスを変更しました', sev: 'success' });
            openDetail(id);
        } catch (e: any) {
            logEvent({ action: 'customer.set_primary_email', target: String(id), result: 'fail', err: e.message });
            setToast({ msg: `変更に失敗しました: ${e.message}`, sev: 'error' });
        }
    };

    const saveMemo = async (id: number, memo: string) => {
        try {
            await call('update', { method: 'POST', body: { id, memo } });
            setToast({ msg: 'メモを保存しました', sev: 'success' });
        } catch (e: any) {
            logEvent({ action: 'customer.update', target: String(id), result: 'fail', err: e.message });
            setToast({ msg: `保存に失敗しました: ${e.message}`, sev: 'error' });
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton onClick={() => navigate('/dashboard')} size="small"><ArrowBackIcon /></IconButton>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>顧客データベース</Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="予約データから顧客を同期します（通常は15分ごとに自動実行されます）">
                    <span>
                        <Button startIcon={<SyncIcon />} onClick={doSync} disabled={busy} size="small" variant="outlined">
                            今すぐ同期
                        </Button>
                    </span>
                </Tooltip>
                <Button startIcon={<RefreshIcon />} onClick={() => { loadStats(); loadList(page, q, sort, dir); }} disabled={loading} size="small">
                    再読込
                </Button>
            </Box>

            {stats && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip label={`顧客 ${stats.customers.toLocaleString()}人`} color="primary" />
                    {/* クリックで一覧を絞り込む（再クリックで解除） */}
                    <Chip label={`リピーター ${stats.repeaters}人`}
                        color={filter === 'repeat' ? 'success' : 'default'}
                        variant={filter === 'repeat' ? 'filled' : 'outlined'}
                        onClick={() => changeFilter('repeat')} />
                    <Chip label={`複数台予約 ${stats.multi_car}人`}
                        color={filter === 'multi_car' ? 'info' : 'default'}
                        variant={filter === 'multi_car' ? 'filled' : 'outlined'}
                        onClick={() => changeFilter('multi_car')} />
                    <Chip label={`グループ ${stats.groups}件`} variant="outlined" />
                    {stats.merged > 0 && <Chip label={`統合済み ${stats.merged}件`} variant="outlined" />}
                    <Chip
                        label={`統合候補 ${stats.candidates}件`}
                        color={stats.candidates > 0 ? 'warning' : 'default'}
                        variant={stats.candidates > 0 ? 'filled' : 'outlined'}
                        onClick={() => setTab(1)}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                        最終同期: {stats.last_synced_at ? new Date(stats.last_synced_at.replace(' ', 'T')).toLocaleString('ja-JP') : '—'}
                    </Typography>
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="顧客一覧" />
                <Tab label={`統合候補${stats?.candidates ? ` (${stats.candidates})` : ''}`} />
            </Tabs>

            {tab === 0 && (
                <>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            size="small" fullWidth placeholder="氏名・メール・電話・予約コードで検索"
                            value={queryInput}
                            onChange={(e) => setQueryInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
                        />
                        <Button variant="contained" startIcon={<SearchIcon />} onClick={doSearch}>検索</Button>
                        {q && <Button onClick={() => { setQueryInput(''); setQ(''); setPage(1); loadList(1, '', sort, dir); }}>クリア</Button>}
                    </Box>

                    {loading && <LinearProgress sx={{ mb: 1 }} />}

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {LIST_COLUMNS.map((h) => (
                                        <TableCell key={h.key} align={h.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            <Tooltip title={h.hint}>
                                                <TableSortLabel
                                                    active={sort === h.key}
                                                    direction={sort === h.key ? dir : 'desc'}
                                                    onClick={() => changeSort(h.key)}
                                                >
                                                    {h.label}
                                                </TableSortLabel>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(r.id)}>
                                        <TableCell>{r.display_name}</TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>
                                            {r.email || '—'}
                                            {r.email_count > 1 && (
                                                <Chip label={`+${r.email_count - 1}`} size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>{r.tel || '—'}</TableCell>
                                        {/* 来店回数は別々の日、台数は同日の複数台を含む。値が違う＝グループがある */}
                                        <TableCell align="right"
                                            sx={{ fontWeight: r.visit_count > 1 ? 700 : 400, color: r.visit_count > 1 ? 'success.main' : undefined }}>
                                            {r.visit_count}
                                        </TableCell>
                                        <TableCell align="right"
                                            sx={{ fontWeight: r.reservation_count > r.visit_count ? 700 : 400, color: r.reservation_count > r.visit_count ? 'info.main' : undefined }}>
                                            {r.reservation_count}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: r.cancelled_count > 0 ? 'text.secondary' : 'text.disabled' }}>
                                            {r.cancelled_count || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{fmtDate(r.last_seen_at)}</TableCell>
                                    </TableRow>
                                ))}
                                {!loading && rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={LIST_COLUMNS.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            該当する顧客がいません
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {total > PER_PAGE && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={Math.ceil(total / PER_PAGE)} page={page}
                                onChange={(_, p) => { setPage(p); loadList(p, q, sort, dir); }}
                            />
                        </Box>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        {total.toLocaleString()} 件
                    </Typography>
                </>
            )}

            {tab === 1 && (
                <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        メールアドレスと氏名（または電話番号）が一致した予約は自動で同一人物にまとめています。
                        ここに出るのは<strong>片方しか一致しなかった</strong>ため機械では決められなかったものです。
                    </Alert>
                    {loading && <LinearProgress sx={{ mb: 1 }} />}
                    {candidates.length === 0 && !loading && (
                        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                            判断待ちの候補はありません
                        </Paper>
                    )}
                    {candidates.map((c) => (
                        <Paper key={c.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip
                                    size="small"
                                    label={c.matched_on === 'email' ? 'メールのみ一致'
                                        : c.matched_on === 'tel' ? '電話のみ一致' : c.matched_on}
                                    color="warning"
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.detail}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {[
                                    { id: c.customer_a, name: c.name_a, email: c.email_a, tel: c.tel_a, res: c.res_a },
                                    { id: c.customer_b, name: c.name_b, email: c.email_b, tel: c.tel_b, res: c.res_b },
                                ].map((p, i) => (
                                    <Paper key={i} variant="outlined" sx={{ p: 1.5, flex: '1 1 280px', bgcolor: 'action.hover' }}>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {p.name}
                                            <Button size="small" sx={{ ml: 1 }} onClick={() => openDetail(p.id)}>詳細</Button>
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{p.email || 'メールなし'}</Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{p.tel || '電話なし'}</Typography>
                                        <Typography variant="caption">予約 {p.res} 件</Typography>
                                    </Paper>
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                                <Button size="small" onClick={() => doReject(c.id)} disabled={busy}>別人として記録</Button>
                                <Button
                                    size="small" variant="contained" startIcon={<MergeTypeIcon />}
                                    onClick={() => setMergeTarget(c)} disabled={busy}
                                >
                                    同一人物として統合
                                </Button>
                            </Box>
                        </Paper>
                    ))}
                </>
            )}

            {/* 統合の確認（取り消せないので方向を明示させる） */}
            <Dialog open={!!mergeTarget} onClose={() => setMergeTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>どちらに統合しますか</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        統合は取り消せません。統合された側の予約・メールアドレス・電話番号はすべて統合先に移ります。
                    </Alert>
                    {mergeTarget && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                variant="outlined" disabled={busy}
                                onClick={() => doMerge(mergeTarget.customer_b, mergeTarget.customer_a)}
                            >
                                「{mergeTarget.name_b}」を「{mergeTarget.name_a}」にまとめる
                            </Button>
                            <Button
                                variant="outlined" disabled={busy}
                                onClick={() => doMerge(mergeTarget.customer_a, mergeTarget.customer_b)}
                            >
                                「{mergeTarget.name_a}」を「{mergeTarget.name_b}」にまとめる
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setMergeTarget(null)}>やめる</Button></DialogActions>
            </Dialog>

            {/* 顧客詳細 */}
            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
                {detail && (
                    <>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {detail.display_name}
                            {detail.visit_count > 1 && <Chip label={`${detail.visit_count}回来店`} size="small" color="success" />}
                            {detail.reservation_count > detail.visit_count && (
                                <Chip label={`同日${detail.reservation_count}台`} size="small" color="info" />
                            )}
                            <Box sx={{ flex: 1 }} />
                            <IconButton size="small" onClick={() => setDetail(null)}><CloseIcon /></IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>メールアドレス</Typography>
                            {detail.emails.length === 0 && <Typography variant="body2" color="text.secondary">登録なし</Typography>}
                            {detail.emails.map((e) => (
                                <Box key={e.email} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title={e.is_primary ? 'メイン' : 'クリックでメインにする'}>
                                        <IconButton size="small" onClick={() => !e.is_primary && setPrimary(detail.id, e.email)}>
                                            {e.is_primary ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant="body2">{e.email}</Typography>
                                    {!!e.is_shared && (
                                        <Tooltip title="他の顧客も同じアドレスを使っています（代表者・家族・法人の可能性）">
                                            <Chip label="共有" size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                                        </Tooltip>
                                    )}
                                    <Typography variant="caption" color="text.secondary">{e.source}</Typography>
                                </Box>
                            ))}

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>電話番号</Typography>
                            {detail.tels.length === 0 && <Typography variant="body2" color="text.secondary">登録なし</Typography>}
                            {detail.tels.map((t) => (
                                <Typography key={t.tel_norm} variant="body2">{t.tel_raw}</Typography>
                            ))}

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                                予約（有効 {detail.reservation_count}台
                                {detail.cancelled_count > 0 && ` / 非予約 ${detail.cancelled_count}件`}）
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableBody>
                                        {detail.reservations.map((r) => {
                                            // status_category 2 = 非予約（キャンセル等）。台数に数えないので薄く表示する
                                            const inactive = Number(r.status_category) === 2;
                                            return (
                                                <TableRow key={`${r.store}-${r.contact_code}`} sx={{ opacity: inactive ? 0.5 : 1 }}>
                                                    <TableCell>
                                                        <Button
                                                            size="small" onClick={() => openReservation(r.contact_code)} disabled={busy}
                                                            sx={{ fontFamily: 'monospace', fontSize: '0.78rem', p: 0, minWidth: 0, textTransform: 'none' }}
                                                        >
                                                            {r.contact_code}
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell sx={{ width: 60 }}>{STORE_LABEL[r.store] || r.store}</TableCell>
                                                    <TableCell sx={{ width: 110 }}>{fmtDate(r.start_at)}</TableCell>
                                                    <TableCell sx={{ width: 110 }}>
                                                        {r.status_code && (
                                                            <Chip
                                                                label={reservationStatusList.find((st: any) => String(st.id) === String(r.status))?.label || r.status_code}
                                                                size="small" color={inactive ? 'default' : 'primary'}
                                                                variant={inactive ? 'outlined' : 'filled'}
                                                                sx={{ height: 18, fontSize: '0.6rem' }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {detail.groups.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>グループ（複数台予約）</Typography>
                                    {detail.groups.map((g) => (
                                        <Paper key={g.group_code} variant="outlined" sx={{ p: 1, mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{g.group_code}</Typography>
                                            <Typography variant="caption" color="text.secondary">{g.members}</Typography>
                                            {g.note && <Alert severity="warning" sx={{ mt: 0.5, py: 0 }}>{g.note}</Alert>}
                                        </Paper>
                                    ))}
                                </>
                            )}

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                                Square 請求書（{detail.invoices.length}件）
                            </Typography>
                            {detail.invoices_error && <Alert severity="warning">{detail.invoices_error}</Alert>}
                            {detail.invoices.length === 0 && !detail.invoices_error && (
                                <Typography variant="body2" color="text.secondary">該当なし</Typography>
                            )}
                            {detail.invoices.length > 0 && (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableBody>
                                            {detail.invoices.map((iv, i) => (
                                                <TableRow key={i}>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{iv.contact_code}</TableCell>
                                                    <TableCell>{iv.status || '—'}</TableCell>
                                                    <TableCell align="right">
                                                        {iv.total_money != null ? `¥${Number(iv.total_money).toLocaleString()}`
                                                            : iv.amount != null ? `¥${Number(iv.amount).toLocaleString()}` : '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ width: 110 }}>{fmtDate(iv.created_at || null)}</TableCell>
                                                    <TableCell sx={{ width: 60 }}>
                                                        {iv.public_url && (
                                                            <Button size="small" href={iv.public_url} target="_blank" rel="noopener noreferrer">開く</Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {detail.mails.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                                        紐づいた受信メール（{detail.mails.length}件）
                                    </Typography>
                                    {detail.mails.map((m) => (
                                        <Typography key={m.mail_id} variant="body2" sx={{ fontSize: '0.8rem' }}>
                                            {fmtDate(m.received_at)} — {m.subject || '(件名なし)'}
                                        </Typography>
                                    ))}
                                </>
                            )}

                            {detail.merged_from.length > 0 && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    統合済み: {detail.merged_from.map((m) => m.display_name).join(' / ')}
                                </Alert>
                            )}

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>メモ</Typography>
                            <TextField
                                multiline minRows={2} fullWidth size="small"
                                defaultValue={detail.memo || ''}
                                onBlur={(e) => { if (e.target.value !== (detail.memo || '')) saveMemo(detail.id, e.target.value); }}
                                helperText="フォーカスを外すと保存されます"
                            />
                        </DialogContent>
                        <DialogActions><Button onClick={() => setDetail(null)}>閉じる</Button></DialogActions>
                    </>
                )}
            </Dialog>

            {/*
              予約サイドパネル（MailQueue と同じ埋め込み。保存は中央経路 saveDispatchReservation のみ）。
              ⚠ サイドパネルの CSS は z-index: 200（gridCalendar.scss）で MUI Dialog の 1300 より下。
                 そのままだと顧客詳細ダイアログに隠れるため、ここで積み重ね文脈を作って
                 グループごと上へ持ち上げる（グローバルな z-index は触らない）。
            */}
            <Box sx={{ position: 'relative', zIndex: (theme) => theme.zIndex.modal + 100 }}>
            <ReservationEditorPanel
                {...{
                    open: !!selectedRes,
                    editing: editingRes,
                    disabled: isSavingRes,
                    onCancel: () => setSelectedRes(null),
                    onClose: () => setSelectedRes(null),
                    onChange: (updates: any) => setEditingRes((prev: any) => ({ ...prev, ...updates })),
                    onSave: async (updated: any, options: any = {}) => {
                        const { keepOpen = false } = options;
                        if (isSavingRes) return;
                        setIsSavingRes(true);
                        try {
                            const saved = await saveDispatchReservation(updated || editingRes);
                            setToast({ msg: '予約を保存しました', sev: 'success' });
                            if (!keepOpen) setSelectedRes(null); else setSelectedRes(saved);
                            // ステータスを変えた場合、台数の数え方が変わるので顧客情報を取り直す
                            if (detail) openDetail(detail.id);
                        } catch (e: any) {
                            logEvent({ action: 'customer.save_reservation', target: String(updated?.contact_code || ''), result: 'fail', err: e?.message });
                            setToast({ msg: `予約の保存に失敗しました: ${e?.message || e}`, sev: 'error' });
                        }
                        setIsSavingRes(false);
                    },
                    registerNameInputRef: () => {},
                    reservationStatusList,
                    vehicles: vehiclesCache,
                    vehicleTypes: vehicleTypeList,
                    onAssignClick: () => {},
                } as any}
            />
            </Box>

            <Snackbar
                open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
            </Snackbar>
        </Box>
    );
};

export default Customers;
